import useContext from '../../PensionPolicyContext/useContext';
import { AbsentTransactionCauseCodeWithMeta, PolicyAbsentTransactionLeave, PolicyLeaveCategory } from '../../types';
import { ABSENT_TRANSACTION_INPUT_MAPPING, INITIAL_POLICY_SPECIAL_CONDITION } from '../../constants';

const useSpecialConditions = () => {
  const { formGroup, updatePolicySettingsInput } = useContext();
  const specialConditionSettings = formGroup?.values.policySettings.specialConditions;

  const probationaryEmployement =
    specialConditionSettings?.probationaryEmployement || INITIAL_POLICY_SPECIAL_CONDITION.probationaryEmployement;
  const parentalLeave = specialConditionSettings?.parentalLeave || INITIAL_POLICY_SPECIAL_CONDITION.parentalLeave;
  const otherLeave = specialConditionSettings?.otherLeave || INITIAL_POLICY_SPECIAL_CONDITION.otherLeave;

  const handleToggleProbationaryEmployeePolicy = (name: string, checked: boolean) => {
    updatePolicySettingsInput(`specialConditions.${PolicyLeaveCategory.PROBATIONARY}.${name}`, checked);
  };

  const handleToggleParentalLeavePolicy = (name: string, checked: boolean) => {
    updatePolicySettingsInput(`specialConditions.${PolicyLeaveCategory.PARENTAL}.${name}`, checked);
  };

  const handleToggleOtherLeavePolicy = (name: string, checked: boolean) => {
    updatePolicySettingsInput(`specialConditions.${PolicyLeaveCategory.OTHER}.${name}`, checked);
  };

  const handleChangeDaysLength = (category: PolicyLeaveCategory, name: string, value: string) => {
    let length: number | null = null;

    if (typeof value === 'string' && !Number.isNaN(Number(value))) {
      length = Number(value);
    }

    updatePolicySettingsInput(`specialConditions.${category}.${name}`, length);
  };

  const isPolicyPartIncluded = (category: PolicyLeaveCategory.PARENTAL | PolicyLeaveCategory.PROBATIONARY) => {
    if (!specialConditionSettings) {
      return false;
    }

    const policyLeaveCategory = specialConditionSettings[category];

    if (!policyLeaveCategory) {
      return false;
    }

    if (
      policyLeaveCategory.isInsurances &&
      policyLeaveCategory.isPremiumCalculation &&
      policyLeaveCategory.isSalaryChange
    ) {
      return true;
    }

    return false;
  };

  const pushToOtherLeave = (transaction: AbsentTransactionCauseCodeWithMeta) => {
    const input : PolicyAbsentTransactionLeave = {
      causeCode: {
        id: transaction.id,
        causeCode: transaction.causeCode,
        english: transaction.english,
        swedish: transaction.swedish,
      },
      isChecked: transaction._internal.checked,
      isPremiumCalculation: transaction._internal.includedIn.premium,
      isSalaryChange: transaction._internal.includedIn.salary,
      isInsurances: transaction._internal.includedIn.insurance,
    }

    updatePolicySettingsInput(`specialConditions.${PolicyLeaveCategory.OTHER}`, [...otherLeave, input]);
  };

  const removeOtherLeave = (id: string) => {
    const updatedOtherLeave = otherLeave.filter((item) => item.causeCode.id !== id);

    updatePolicySettingsInput(`specialConditions.${PolicyLeaveCategory.OTHER}`, updatedOtherLeave);
  };


  const toggleCheckedOtherLeaveIncludedIn = (id: string, name: keyof AbsentTransactionCauseCodeWithMeta['_internal']['includedIn'], checked: boolean)=>{
    const propName = ABSENT_TRANSACTION_INPUT_MAPPING[name];

    const updatedOtherLeave = otherLeave.map((item) => {
      if (item.causeCode.id === id) {
        return {
          ...item,
          [propName]: checked,
        };
      }
      return item;
    });

    updatePolicySettingsInput(`specialConditions.${PolicyLeaveCategory.OTHER}`, updatedOtherLeave);
  }

  return {
    probationaryEmployement,
    parentalLeave,
    otherLeave,

    handleToggleProbationaryEmployeePolicy,
    handleToggleParentalLeavePolicy,
    handleToggleOtherLeavePolicy,
    handleChangeDaysLength,
    isPolicyPartIncluded,

    pushToOtherLeave,
    removeOtherLeave,
    toggleCheckedOtherLeaveIncludedIn
  };
};

export default useSpecialConditions;
